import {
    NOTIFICATION_TYPE_INFO,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARN,
} from '../../../constants';
import { getCMSConfig } from '../../../util/cms';
import { formatDownload, formatProductName } from '../../../util/formatter';
import { AccessSupplier, AvailabilityIndicator, HomesPassedType, ProductType } from '../../../enums/product';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { getOptionPageUrl, getProductPageUrl } from '../../../util/url';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { INotificationProperties } from '@vfde-brix/ws10/notification';
import { setOverlayVisiblity } from '../../../components/overlay';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';

/**
 * Gets the tooltip config for the avc notification
 * if selected is available and there is regioFee
 * @param avcResult Result of the avc
 * @returns Tooltip config for avc notication
 */
export const getAvcNotificationTooltip = (
    { selectedAvailable, selected, availability, fastest }: AvcResult
) => {
    const config = getCMSConfig().avcResult;
    const isFibRegioCharge = availability.fiber && fastest?.regioCharge;
    const isPreFibRegioCharge = selectedAvailable && selected.regioCharge;
    const isRegioCharge = isPreFibRegioCharge || isFibRegioCharge;

    return isRegioCharge ? config.preFiber.available.regioFee.tooltip : null;
};

/**
 * Gets the props that need to be updated
 * on the notification component
 * @param avcResult Result of the avc
 * @returns Notification props to update
 */
export const getAvcNotificationProps = (
    { selectedAvailable, selected, fastest, availability }: AvcResult,
    isPreFiber: boolean,
    homesPassedType?: HomesPassedType,
    accessSupplier?: string,
    availabilityIndicator?: string,
): Partial<INotificationProperties> => {
    const { fiber, homesPassed, VFDE } = getCMSConfig().avcResult;
    const preFiber = getCMSConfig().avcResult.preFiber;
    const { available, slowerAvailable, notAvailable } = preFiber;
    const { headline, subline } = fiber;

    const isFiberAvailable = availability.fiber;
    const isVFDE = accessSupplier === AccessSupplier.VFDE;
    const isGruen = availabilityIndicator === AvailabilityIndicator.GRUEN;
    const isGelpGrau = availabilityIndicator === AvailabilityIndicator.GELB_GRAU;

    if (VFDE && isVFDE && isGelpGrau) {
        return {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: formatProductName(
                VFDE.headline,
                selectedAvailable ? selected?.name : fastest?.name
            ),
            txtContent: VFDE.subline,
        };
    }

    if (homesPassedType) {
        return homesPassedType === HomesPassedType.BHS ? {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: homesPassed.bhs.headline,
            txtContent: homesPassed.bhs.subline,
        } : {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: homesPassed.bkv.headline,
            txtContent: homesPassed.bkv.subline,
        };
    }

    if (selectedAvailable) {
        return {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: formatProductName(available.text, selected?.name),
            txtContent: formatDownload(available.fastest, fastest?.download),
        };
    }

    if (isPreFiber) {
        return {
            optState: NOTIFICATION_TYPE_INFO,
            stdHeadline: formatProductName(slowerAvailable.text, fastest?.name),
            txtContent: formatDownload(slowerAvailable.slower, fastest?.download),
        };
    }

    if (isFiberAvailable && (isVFDE || isGruen)) {
        return {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: formatProductName(headline, fastest?.name),
            txtContent: formatDownload(subline, fastest?.download),
        };
    }

    return {
        optState: NOTIFICATION_TYPE_WARN,
        stdHeadline: formatProductName(notAvailable.text, selected.name),
        txtContent: notAvailable?.subline && formatDownload(notAvailable.subline, fastest?.download),
    };
};

const getPreFiberAvcResultBtnProps = (
    tariffId: string,
    { regioCharge }: JarvisProduct,
): Partial<IButtonProperties> => {
    const { buttonRedirect, regioFee } = getCMSConfig().avcResult.preFiber.available;
    const { minimumChargeEuro } = getCMSConfig().regioFeeBenefit;

    // redirect to option page
    if (regioCharge && regioCharge <= +minimumChargeEuro) {
        return {
            stdContent: buttonRedirect,
            linkHref: getOptionPageUrl(tariffId),
        };
    }

    // in case of regio fee is bigger than the minimum amount,
    // the result button have to close the overlay to show the new price
    return {
        stdContent: regioFee.button,
        linkHref: undefined,
        business: { onClick: () => setOverlayVisiblity(false) },
    };
};

/**
 * Gets the props that need to be
 * updated for the avc result button
 * @param tariffId - Id of the selected tariff
 * @param product - Fastest available product
 */
export const getAvcResultButtonProps = (
    tariffId: string,
    isPreFiber: boolean,
    product?: JarvisProduct,
): Partial<IButtonProperties> => {
    if (product && isPreFiber) {
        return getPreFiberAvcResultBtnProps(tariffId, product);
    }

    const { fiber, cable, dsl, gigacube } = getCMSConfig().avcResult;
    const { button } = (() => {
        switch (product?.productType) {
            case ProductType.FIBER:
                return fiber;
            case ProductType.CABLE:
                return cable;
            case ProductType.DSL:
                return dsl;
            default:
                return gigacube;
        }
    })();

    return {
        linkHref: product?.productType
            ? getProductPageUrl(product.productType, product.id)
            : gigacube.link,
        stdContent: button,
        business: NO_PATTERN_BUSINESS_LOGIC,
    };
};

/**
 * Gets the text to be shown for an alternative
 * product when fiber is not available
 * @param fastest fastest available tariff
 * @returns Formatted alternative text
 */
export const getAvcResultAlternativeText = (fastest?: JarvisProduct) => {
    const config = getCMSConfig().avcResult;

    switch (fastest?.productType) {
        case ProductType.FIBER:
            return;
        case ProductType.CABLE:
            return formatDownload(config.cable.text, fastest.download);
        case ProductType.DSL:
            return formatDownload(config.dsl.text, fastest.download);
        default:
            return config.gigacube.text;
    }
};
